import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["assetDetails", "assetScopes", "disabled", "offeringType", "section"];
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PortalIdParser from 'PortalIdParser';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { useAssetInstallability } from 'marketplace-ui-assets-core/hooks/useAssetInstallability';
import { useAssetInstallabilityMessage } from 'marketplace-ui-assets-core/hooks/useAssetInstallabilityMessage';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { selectPortalScopes, selectUserScopes } from '../../selectors/auth';
import { getAppContextCoreType, getIsPublicApp } from '../../selectors/app';
import { getAssetEventSelectorId } from '../../utils/hsAnalytics';
import { getDownloadedAssetsUpdateUrl } from 'marketplace-ui-assets-core/utils/urls';
import AssetOrderSummaryModal from './AssetOrderSummaryModal';
import AssetSignInButton from './AssetSignInButton';
import CurrencyText from '../../details/components/CurrencyText';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AssetDownloadButton = _ref => {
  let {
      assetDetails,
      assetScopes,
      disabled,
      offeringType,
      section
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    offeringId,
    pricing,
    slug
  } = assetDetails;
  const {
    trackDetailsInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const appContext = useSelector(getAppContextCoreType);
  const portalScopes = useSelector(selectPortalScopes);
  const userScopes = useSelector(selectUserScopes);
  const assetInstallabilityResult = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    installability
  } = assetInstallabilityResult;
  const {
    installed,
    canUpdate
  } = installability || {};
  const isUpdateAvailable = installed && canUpdate;
  const isFree = pricing.planType === PlanType.FREE;
  const {
    hasAccess,
    message
  } = useAssetInstallabilityMessage(Object.assign({}, assetInstallabilityResult, {
    offeringId,
    offeringSlug: slug,
    offeringType,
    portalId: PortalIdParser.get()
  }));
  const isMissingAssetScopes = assetScopes == null && !isFree;
  const isInstallable = hasAccess && !isMissingAssetScopes;
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const handleClick = useCallback(() => {
    setOrderSummaryOpen(true);
    trackDetailsInteraction('click install button', {
      section
    });
  }, [section, trackDetailsInteraction]);
  const computedButtonProps = useMemo(() => {
    if (isUpdateAvailable) {
      return {
        href: getDownloadedAssetsUpdateUrl(offeringId, offeringType)
      };
    }
    return {};
  }, [isUpdateAvailable, offeringId, offeringType]);
  const renderedButtonText = useMemo(() => {
    if (isUpdateAvailable) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.details.downloadButton.updateAvailable",
        options: {
          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
        }
      });
    }
    if (installed) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.details.downloadButton.installed",
        options: {
          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
        }
      });
    }
    if (isFree) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.details.downloadButton.free"
      });
    }
    return isMobileView ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "marketplace-assets-ui.details.downloadButton.paidWithPrice",
      options: {
        price: /*#__PURE__*/_jsx(CurrencyText, {
          cents: pricing.cents
        })
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.details.downloadButton.paid",
      options: {
        offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}_lowercase`)
      }
    });
  }, [offeringType, isUpdateAvailable, installed, isFree, isMobileView, pricing]);
  return isPublicApp ? /*#__PURE__*/_jsx(AssetSignInButton, Object.assign({}, rest, {
    isFree: isFree,
    offeringType: offeringType,
    section: section,
    slug: slug
  })) : /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip, {
      disabled: !message,
      title: message,
      children: /*#__PURE__*/_jsx(Button, Object.assign({}, rest, computedButtonProps, {
        "data-hs-analytics-id": getAssetEventSelectorId('DOWNLOAD', offeringType),
        "data-installability-loaded": installability !== null,
        disabled: disabled || !isInstallable,
        onClick: handleClick,
        use: "primary",
        children: renderedButtonText
      }))
    }), orderSummaryOpen && /*#__PURE__*/_jsx(AssetOrderSummaryModal, {
      assetDetails: assetDetails,
      offeringType: offeringType,
      onClose: () => setOrderSummaryOpen(false)
    })]
  });
};
export default AssetDownloadButton;